import React, { useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Box,
  Alert,
  Button,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import { websocketService } from '../../services/websocketService';

const AdministrativeObservations = () => {
  const [observations, setObservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [observatoryFilter, setObservatoryFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [userObservatories, setUserObservatories] = useState([]);
  const [observationUsers, setObservationUsers] = useState([]);
  const navigate = useNavigate();

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'assigned', label: 'Assigned' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'completed_series', label: 'Completed Series' },
    { value: 'partially_completed', label: 'Partially Completed' },
    { value: 'waiting_next', label: 'Waiting Next' },
    { value: 'failed', label: 'Failed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'aborted', label: 'Aborted' },
    { value: 'expired', label: 'Expired' },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'completed_series':
        return 'success';
      case 'partially_completed':
        return 'secondary';
      case 'in_progress':
      case 'assigned':
      case 'acquisition_complete':
        return 'primary';
      case 'pending':
      case 'waiting_next':
        return 'info';
      case 'failed':
      case 'aborted':
      case 'expired':
        return 'error';
      case 'cancelled':
      case 'paused':
      case 'suspended':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatUtcDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const d = new Date(dateString);
      const month = (d.getUTCMonth() + 1).toString().padStart(2, '0');
      const day = d.getUTCDate().toString().padStart(2, '0');
      const hours = d.getUTCHours().toString().padStart(2, '0');
      const minutes = d.getUTCMinutes().toString().padStart(2, '0');
      const seconds = d.getUTCSeconds().toString().padStart(2, '0');
      return `${month}/${day} ${hours}:${minutes}:${seconds}`;
    } catch { return '-'; }
  };

  const formatStartTime = (observation) => {
    if (observation.observation_type === 'fixed_time' && observation.fixed_time_start) {
      return formatUtcDate(observation.fixed_time_start);
    }
    // started_at is the actual execution start (set once running); fall back to the planned window.
    return formatUtcDate(
      observation.started_at || observation.utc_start_time || observation.constraints?.start_time
    );
  };

  const TERMINAL_WITH_COMPLETED_AT = ['completed', 'completed_series', 'partially_completed', 'failed', 'aborted', 'expired'];

  const formatEndTime = (observation) => {
    if (observation.status === 'in_progress') return '-';
    if (TERMINAL_WITH_COMPLETED_AT.includes(observation.status) && observation.completed_at) {
      return formatUtcDate(observation.completed_at);
    }
    if (observation.observation_type === 'fixed_time' && observation.fixed_time_end) {
      return formatUtcDate(observation.fixed_time_end);
    }
    return formatUtcDate(observation.utc_end_time || observation.constraints?.end_time);
  };

  const loadUserObservatories = async () => {
    try {
      const response = await apiService.get('/v1/observatories');
      if (response.data.success) {
        setUserObservatories(response.data.data.observatories || []);
      }
    } catch (error) {
      console.error('Error loading user observatories:', error);
    }
  };

  const loadObservationUsers = async () => {
    try {
      const response = await apiService.get('/v1/observations/administrative/users');
      if (response.data.success) {
        setObservationUsers(response.data.data.users || []);
      }
    } catch (error) {
      console.error('Error loading observation users:', error);
    }
  };

  const loadObservations = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query parameters
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (observatoryFilter) params.append('observatory_id', observatoryFilter);
      if (userFilter) params.append('created_by', userFilter);
      params.append('limit', '100'); // Get more observations for admin view

      const endpoint = tabValue === 0 
        ? '/v1/observations/administrative' // Observatory admin observations
        : '/v1/observations'; // User's own observations
      
      const url = params.toString() ? `${endpoint}?${params}` : endpoint;
      const response = await apiService.get(url);

      if (response.data.success) {
        const observationData = response.data.data?.observations || [];
        // Filter out any null or invalid observations
        const validObservations = observationData.filter(obs => obs && obs._id);
        setObservations(validObservations);
      } else {
        setError(response.data.message || 'Failed to load observations');
      }
    } catch (error) {
      console.error('Error loading observations:', error);
      setError(error.response?.data?.message || 'Failed to load observations');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadUserObservatories();
    loadObservationUsers();
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadObservations();
  }, [tabValue, statusFilter, observatoryFilter, userFilter]);

  // WebSocket listener for real-time observation updates
  useEffect(() => {
    const handleObservationUpdate = async (data) => {
      console.log('WebSocket observation update received:', data);
      const observationId = data.observation_id || data._id || data.id;

      try {
        // Fetch the updated observation
        const response = await apiService.get(`/v1/observations/${observationId}`);
        if (response.data.success) {
          const updatedObservation = response.data.data;

          // Update the observation in state
          setObservations(prev => {
            const index = prev.findIndex(obs => obs._id === observationId);
            if (index !== -1) {
              const newObservations = [...prev];
              newObservations[index] = updatedObservation;
              return newObservations;
            }
            return prev;
          });
        }
      } catch (error) {
        console.error('Failed to fetch updated observation:', error);
      }
    };

    const handleObservationCreated = async (data) => {
      console.log('WebSocket observation_created received:', data);
      const observationId = data.observation_id || data._id || data.id;

      try {
        const response = await apiService.get(`/v1/observations/${observationId}`);
        if (response.data.success) {
          const newObservation = response.data.data;
          setObservations(prev => {
            // Avoid duplicates
            if (prev.some(obs => obs._id === newObservation._id)) return prev;
            return [newObservation, ...prev];
          });
        }
      } catch (error) {
        console.error('Failed to fetch new observation:', error);
      }
    };

    // Register the WebSocket listeners
    websocketService.on('observation_created', handleObservationCreated);
    websocketService.on('observation_started', handleObservationUpdate);
    websocketService.on('observation_completed', handleObservationUpdate);
    websocketService.on('observation_updated', handleObservationUpdate);
    websocketService.on('observation_assigned', handleObservationUpdate);

    // Cleanup
    return () => {
      websocketService.off('observation_created', handleObservationCreated);
      websocketService.off('observation_started', handleObservationUpdate);
      websocketService.off('observation_completed', handleObservationUpdate);
      websocketService.off('observation_updated', handleObservationUpdate);
      websocketService.off('observation_assigned', handleObservationUpdate);
    };
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleViewObservation = (observationId) => {
    navigate(`/observations/${observationId}`, { state: { from: 'admin' } });
  };

  const handleRefresh = () => {
    loadObservations();
  };

  if (loading && observations.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h4" component="h1">
            Observations
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          View observations for observatories you own or administer, plus your own observations.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Paper sx={{ width: '100%' }}>
          <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab label="Observatory Observations" />
            <Tab label="My Observations" />
          </Tabs>

          <Box sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Observatory</InputLabel>
                <Select
                  value={observatoryFilter}
                  onChange={(e) => setObservatoryFilter(e.target.value)}
                  label="Observatory"
                >
                  <MenuItem value="">All Observatories</MenuItem>
                  {userObservatories.map((observatory) => (
                    <MenuItem key={observatory._id} value={observatory._id}>
                      {observatory.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {tabValue === 0 && (
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Submitted By</InputLabel>
                  <Select
                    value={userFilter}
                    onChange={(e) => setUserFilter(e.target.value)}
                    label="Submitted By"
                  >
                    <MenuItem value="">All Users</MenuItem>
                    {observationUsers.map((user) => (
                      <MenuItem key={user._id} value={user._id}>
                        {user.username || user.email || `${user.first_name} ${user.last_name}`.trim()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Target</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Observatory</TableCell>
                    <TableCell>Start Time</TableCell>
                    <TableCell>End Time</TableCell>
                    <TableCell>Created By</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {observations.length === 0 && !loading ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No observations found matching the current filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    observations.filter(observation => observation && observation._id).map((observation) => (
                      <TableRow
                        key={observation._id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleViewObservation(observation._id)}
                      >
                        <TableCell>{observation.name || 'Unnamed'}</TableCell>
                        <TableCell>{observation.targets?.[0]?.name || observation.target_name || '-'}</TableCell>
                        <TableCell>
                          <Chip label={observation.priority || '-'} variant="outlined" size="small" />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={String(observation.status || 'Unknown').replace(/_/g, ' ')}
                            color={getStatusColor(observation.status)}
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell>{observation.assigned_to?.name || 'Unassigned'}</TableCell>
                        <TableCell>{formatStartTime(observation)}</TableCell>
                        <TableCell>{formatEndTime(observation)}</TableCell>
                        <TableCell>{observation.created_by?.username || observation.created_by?.email || 'Unknown'}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); handleViewObservation(observation._id); }}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {loading && observations.length > 0 && (
              <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
                <CircularProgress size={20} />
              </Box>
            )}
          </Box>
        </Paper>
    </Box>
  );
};

export default AdministrativeObservations;